/**
 * channelService — web read/write layer for channel links (PPL-464).
 *
 * Mirrors `writebackService`'s split, for the same reason: the owner-readable
 * projection `users/{uid}/channelLinks/{linkKey}` is read DIRECTLY over
 * onSnapshot, and every mutation goes through a callable because the binding
 * doc (root `channelLinks/{linkKey}`, which holds the raw external identity)
 * is Admin-only in both directions.
 *
 * Two rules worth keeping when this file changes:
 *
 *  - **Live, not fetched.** The connect flow finishes on the user's PHONE. The
 *    row can only flip to "connected" while they are looking at the browser if
 *    it is a subscription, so this module never exposes a one-shot read.
 *  - **The code is opaque.** `code` is displayed and copied, never parsed or
 *    validated for shape — A4 is changing the format, and a client-side format
 *    assumption would silently reject a valid code.
 */

import {
  collection,
  onSnapshot,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import {
  FIRESTORE_DB,
  createChannelLinkCodeEurW,
  revokeChannelLinkEurW,
} from '../firebaseConfig';
import type {
  ChannelErrorKind,
  ChannelLink,
  ChannelLinkCode,
  ChannelName,
} from '../types/channels';

/* ------------------------------------------------------------------ *
 * Writes — callables only
 * ------------------------------------------------------------------ */

/**
 * Mint a one-time WhatsApp link code for the signed-in user.
 *
 * Throws the raw callable error so the caller can map it: `failed-precondition`
 * is the channel kill switch (also what an unreadable config degrades to —
 * fail-closed by construction server-side), `unauthenticated` a lapsed session.
 */
export async function createWhatsappLinkCode(): Promise<ChannelLinkCode> {
  const res = await createChannelLinkCodeEurW({ channel: 'whatsapp' });
  const data = (res?.data || {}) as Partial<ChannelLinkCode>;
  if (typeof data.code !== 'string' || !data.code) {
    throw new Error('malformed link code response');
  }
  return {
    code: data.code,
    expiresAt: typeof data.expiresAt === 'string' ? data.expiresAt : '',
    channel: 'whatsapp',
    // Only an https link ever reaches an anchor href. The value is our own
    // server's, but a hand-edited config should not be able to put a
    // javascript:/data: URL in front of a user.
    deepLink: isSafeDeepLink(data.deepLink) ? data.deepLink : null,
  };
}

/**
 * Revoke one of the caller's links. Idempotent server-side; the projection is
 * kept with `status: 'revoked'` rather than deleted, so the row goes back to
 * "not connected" through the SAME snapshot the connect flow flips.
 */
export async function revokeChannelLink(linkKey: string): Promise<boolean> {
  const res = await revokeChannelLinkEurW({ linkKey });
  return (res?.data as { success?: boolean })?.success === true;
}

/* ------------------------------------------------------------------ *
 * Reads — a live subscription
 * ------------------------------------------------------------------ */

function toDate(ts: unknown): Date | null {
  if (!ts) return null;
  const t = ts as Timestamp;
  if (typeof t.toDate === 'function') return t.toDate();
  const d = new Date(ts as string);
  return Number.isNaN(d.getTime()) ? null : d;
}

function normalizeLink(id: string, raw: Record<string, any>): ChannelLink {
  const settings = (raw.settings || {}) as Record<string, unknown>;
  return {
    linkKey: id,
    channel: (raw.channel === 'telegram' ? 'telegram' : 'whatsapp') as ChannelName,
    status: raw.status === 'active' ? 'active' : 'revoked',
    label: typeof raw.label === 'string' ? raw.label : '',
    linkedAt: toDate(raw.linkedAt),
    revokedAt: toDate(raw.revokedAt),
    threadId: typeof raw.threadId === 'string' ? raw.threadId : null,
    settings: {
      // Absent means on, matching the server's absent-means-enabled semantics.
      writeback: settings.writeback !== false,
      notifications: settings.notifications !== false,
    },
  };
}

/**
 * Live ACTIVE links for one user, newest first.
 *
 * The whole subcollection is subscribed and filtered in memory rather than
 * queried with `where('status','==','active') + orderBy('linkedAt','desc')`:
 * that pair needs a composite index, none is deployed for `channelLinks`, and
 * a missing index returns NOTHING — indistinguishable from "not connected",
 * which is exactly the lie this row must never tell. The collection holds one
 * doc per channel identity, so there is nothing to page.
 */
export function subscribeChannelLinks(
  uid: string,
  onData: (links: ChannelLink[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    collection(FIRESTORE_DB, 'users', uid, 'channelLinks'),
    (snap) => {
      const rows: ChannelLink[] = [];
      snap.forEach((d) => {
        const link = normalizeLink(d.id, d.data() as Record<string, any>);
        if (link.status === 'active') rows.push(link);
      });
      rows.sort((a, b) => (b.linkedAt?.getTime() || 0) - (a.linkedAt?.getTime() || 0));
      onData(rows);
    },
    (error) => {
      console.warn('[channels] link subscription failed:', error?.message);
      if (onError) onError(error);
    },
  );
}

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

function isSafeDeepLink(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('https://');
}

/**
 * The business number to send the code to, pulled back out of the deep link
 * (`https://wa.me/<digits>?text=…`) — the server sends the number exactly
 * once, in that link.
 *
 * Rendered as `+<digits>` with no grouping: block sizes are country-specific,
 * and a confidently wrong grouping of a number the user is about to message is
 * worse than an ungrouped one. Null when there is no link to read it from.
 */
export function phoneFromDeepLink(deepLink: string | null): string | null {
  if (!deepLink) return null;
  const m = deepLink.match(/wa\.me\/(\d{6,20})/);
  return m ? `+${m[1]}` : null;
}

/** Milliseconds left on a code, floored at 0. Unparseable expiry → 0. */
export function msUntilExpiry(expiresAt: string, now: number = Date.now()): number {
  const t = new Date(expiresAt).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, t - now);
}

/**
 * Callable error → the flavour of message to show.
 *
 * `failed-precondition` (channel disabled or its config unreadable) and
 * `permission-denied` (a bot account) are both "not available", deliberately:
 * the user can do nothing about either, and spelling out which is which tells
 * them nothing useful. Everything else is transient enough to retry.
 */
export function channelErrorKind(err: unknown): ChannelErrorKind {
  const code = (err as { code?: string } | null)?.code || '';
  return code.includes('failed-precondition') || code.includes('permission-denied')
    ? 'unavailable'
    : 'retry';
}
